import { parse } from "date-fns";

import {
  ILicense,
  ILicenseFlags,
  ILicenseSettings,
  ILicenseSourceParams,
  ILicenseSubscription,
} from "store/entities/license";
import { Status } from "store/entities/subscriptions";

function deserializeFlags(flags: ILicenseFlags): ILicenseFlags {
  return {
    defaultInboxUsed: flags.defaultInboxUsed,
    customDomainAdded: flags.customDomainAdded || false,
    firstTeamAdded: flags.firstTeamAdded || false,
    firstAgentAdded: flags.firstAgentAdded || false,
    firstTicketResponded: flags.firstTicketResponded || false,
    knowledgeBaseIntegrationPreinstalled: flags.knowledgeBaseIntegrationPreinstalled || false,
    signupGoogleAnalyticsEventSent: flags.signupGoogleAnalyticsEventSent || false,
  };
}

function deserializeSubscription(subscription: { [key: string]: unknown }): ILicenseSubscription {
  return {
    state: subscription.state as Status,
    pastDue: (subscription.pastDue as boolean) || false,
  };
}

function deserializeSettings(settings: ILicenseSettings): ILicenseSettings {
  return {
    companyName: settings.companyName || "",
    replyAddress: settings.replyAddress,
    defaultMailboxDescription: settings.defaultMailboxDescription,
    ticketThread: settings.ticketThread || false,
    supportUkraineFooter: settings.supportUkraineFooter || false,
    customFields: new Set(settings.customFields || []),
    poweredByFooter: settings.poweredByFooter,
    textEnhancements: settings.textEnhancements || false,
    moveToArchiveAfterDays: settings.moveToArchiveAfterDays,
    moveToTrashAfterDays: settings.moveToTrashAfterDays,
    purgeTrashAfterDays: settings.purgeTrashAfterDays,
    purgeSpamAfterDays: settings.purgeSpamAfterDays,
    ticketSummary: settings.ticketSummary || false,
    ticketFeedToolbar: settings.ticketFeedToolbar || false,
    invitationModeMigration: settings.invitationModeMigration || false,
    managedByPartner: settings.managedByPartner || false,
    partnersContactEmail: settings.partnersContactEmail || "",
    featureToggleMacros: settings.featureToggleMacros || false,
    maxWebpushSubscriptionsPerAgent: settings.maxWebpushSubscriptionsPerAgent || 0,
    maxSubscriptionsPerSocket: settings.maxSubscriptionsPerSocket || 20,
    maxSharedMacros: settings.maxSharedMacros,
    maxPrivateMacrosPerAgent: settings.maxPrivateMacrosPerAgent,
    maxCustomFieldsActive: settings.maxCustomFieldsActive,
    maxTemplatesPerLicense: settings.maxTemplatesPerLicense,
    maxContactForms: settings.maxContactForms,
    maxEmailDomains: settings.maxEmailDomains,
    maxMailboxes: settings.maxMailboxes,
    maxPaidAgents: settings.maxPaidAgents,
    maxReplyAddresses: settings.maxReplyAddresses,
    maxRulesActive: settings.maxRulesActive,
    maxTeams: settings.maxTeams,
    maxChildTicketsPerTicket: settings.maxChildTicketsPerTicket,
    auditLog: settings.auditLog || false,
    googleForwarding: settings.googleForwarding,
    autoassignmentManagedByAgents: settings.autoassignmentManagedByAgents || false,
    showAgentTagChangeEvents: settings.showAgentTagChangeEvents || false,
    customReportGenerateAllowed: settings.customReportGenerateAllowed || false,
    featureToggleProductSwitcher: settings.featureToggleProductSwitcher || false,
    featureTogglePerformanceReport: settings.featureTogglePerformanceReport || false,
    featureToggleSortOrder: settings.featureToggleSortOrder || false,
    featureToggleTagSuggestions: settings.featureToggleTagSuggestions || false,
    featureToggleSimilarTickets: settings.featureToggleSimilarTickets || false,
    featureToggleDarkMode: settings.featureToggleDarkMode || false,
    featureToggleReplySuggestions: settings.featureToggleReplySuggestions || false,
  };
}

function deserializeSource(source: ILicenseSourceParams): ILicenseSourceParams {
  const {
    utmSource,
    utmMedium,
    utmCampaign,
    utmContent,
    utmTerm,
    referrer,
    landingPage,
    sourceURL,
    sourceID,
    sourceType,
  } = source;

  return {
    utmSource,
    utmMedium,
    utmCampaign,
    utmContent,
    utmTerm,
    referrer,
    landingPage,
    sourceURL,
    sourceID,
    sourceType,
  };
}

export default abstract class LicenseSerializer {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  static deserialize(data: { [key: string]: any }): ILicense {
    return {
      id: data.ID,
      createdAt: parse(data.createdAt),
      createdBy: data.createdBy,
      updatedAt: parse(data.updatedAt),
      updatedBy: data.updatedBy,
      defaultTeamId: data.defaultTeamID,
      flags: deserializeFlags(data.flags),
      subscription: deserializeSubscription(data.subscription),
      settings: deserializeSettings(data.settings),
      source: deserializeSource(data.source),
      contactFormDefaults: data.settings.contactFormDefaults,
      verifiedAt: parse(data.verifiedAt),
      verifiedBy: data.verifiedBy,
      defaultTemplateId: data.defaultTemplateID,
      detectedLanguages: data.detectedLanguages,
      availableSources: data.availableSources,
      type: data.type,
    };
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  static deserializeCollection(data: { [key: string]: any }[]): ILicense[] {
    return data.map(LicenseSerializer.deserialize);
  }
}
